/**
 * What this session changed, as a control in the session's header.
 *
 * It answers the question you ask on arriving at a session somebody else (or
 * you, yesterday) was running — what did this actually touch — which is
 * otherwise answerable only by scrolling the whole transcript and reading the
 * tool calls one at a time.
 *
 * A COUNT AND A MENU, not a strip. It used to be a collapsible band across the
 * top of the thread, which spent a row of the conversation on a number even
 * when nobody was asking, and pushed the messages down when opened. In the
 * header it costs nothing until clicked, and the list floats over the thread
 * rather than displacing it.
 *
 * TOP RIGHT, LEFT OF THE SPLIT CONTROL. The left of that row is what this
 * session IS (back, title); the right is what you can DO with it. A summary of
 * its work belongs with the verbs, and next to Split rather than past it,
 * because the pane controls are the last thing in the row in every pane.
 *
 * ONE WIDGET IN A SLOT, not the whole header. It fills `sessionHeader`
 * (`chrome/sessionHeaderSlot.tsx`) as a built-in, beside whatever else an
 * installed app puts there. It was briefly gated on the Review app being
 * installed, on the reasoning that reviewing a change is Review's job; that was
 * wrong twice over. What a session changed is on the log whether or not
 * anything is installed, and a fresh project saw an empty header with no way
 * to find out why.
 *
 * HONEST ABOUT WHAT IT DID NOT SEE. The transcript is windowed (a long session
 * loads recent pages only), so the fold can be short. The menu says so at its
 * foot rather than reporting a count it cannot stand behind: an undercount that
 * looks exact is worse here than an admitted gap, because the whole point of
 * the control is to be the list you did not have to scroll for.
 */

import { ChevronDown, FileCode2, FileText, GitBranch } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useProjectFolderPath } from '../../services/code/projectFolder'
import { groupChangedFiles } from './changedFileGroups'
import { fileLabel } from './SessionChangesPanel'
import type { SessionChanges as Changes } from './sessionChanges'

export function SessionChangesView({
  projectId,
  changes,
  documents,
  partial,
  onOpenDocument,
  onViewMore,
  fileSourceId,
  reviewBase,
  reviewBranch,
  onReviewBranch,
}: {
  /** The session's project: a file under its folder opens and is named by
   *  its place in it, even when the write did not record a branch. */
  projectId?: string
  changes: Changes
  /** The project catalog, for titles. A create call names a path and an
   *  update call names nothing at all, so without this half the rows would be
   *  ids. Structurally typed, not `DocumentRef`: the slot's props carry the two
   *  fields this needs, and taking the catalog's whole row would make every
   *  caller find one. */
  documents?: { id: string; title: string }[]
  /** Earlier transcript pages exist and were not read. */
  partial?: boolean
  /** Put a document on the canvas. Absent in a context that cannot open one,
   *  in which case the rows are text. */
  onOpenDocument?: (documentId: string) => void
  /** Open the full Changes view in place of the thread (RFC 0313 3h). */
  onViewMore?: () => void
  fileSourceId?: string
  /** The integration branch, when known for these writes. Its own group
   *  offers no review: there is nothing to compare it against. */
  reviewBase?: string
  /** Limit the action when several Git sources make another branch ambiguous. */
  reviewBranch?: string
  /** Open the review page for a branch. Absent where none can be made. */
  onReviewBranch?: (branch: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const folder = useProjectFolderPath(projectId)

  // Closes on an outside click and on Escape, like the composer's menus. A
  // floating list in a header that stayed open while you worked elsewhere is a
  // panel nobody asked for, covering the thread it is about.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Grouped by the branch each write recorded when it landed (RFC 0329 3a).
  // Nothing is asked here: the log already says. With no records at all the
  // rows are the flat list they always were.
  const groups = useMemo(
    () => groupChangedFiles(changes.files),
    [changes.files],
  )
  const headed = changes.files.some((f) => f.writtenOn)
  const baseBranch = reviewBase
    ?.replace(/^refs\/heads\//, '')
    .replace(/^origin\//, '')

  const titles = useMemo(() => {
    const map = new Map<string, string>()
    for (const doc of documents ?? []) map.set(doc.id, doc.title)
    return map
  }, [documents])

  // ONE NUMBER, files and documents together. The button is a word wide in a
  // row of icons, and "3 files · 2 documents" there reads as a sentence
  // somebody left in the toolbar. The split is in the menu, where the rows are.
  const count = changes.files.length + changes.documents.length
  if (count === 0) return null

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="What this session created or edited"
        className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:bg-muted hover:text-foreground ${
          open ? 'bg-muted text-foreground' : 'text-muted-foreground'
        }`}
      >
        {/* RFC 0313 3h: ± before the count, beside Where and the tasks tick. */}
        <span>± {count}</span>
        <ChevronDown className="size-3 shrink-0" strokeWidth={2.5} />
      </button>
      {open ? (
        <div
          role="menu"
          // RIGHT-ALIGNED, because the control sits at the right edge of the
          // header and a list hung from its left would run off the pane in a
          // split.
          className="absolute top-full right-0 z-20 mt-1.5 max-h-80 min-w-72 max-w-96 overflow-y-auto rounded-xl border border-border bg-background py-1 shadow-lg"
        >
          {groups.map((group) => (
            <div key={group.branch ?? ''}>
              {/* A heading only once the branches are known. Before that, or
                  with no git server, the rows are the flat list they were. */}
              {headed ? (
                <div className="flex items-center gap-1.5 px-3 pt-1.5 pb-0.5 text-[11px] text-muted-foreground">
                  {group.branch ? (
                    <GitBranch className="size-3 shrink-0" />
                  ) : null}
                  <span className="truncate">
                    {group.branch ?? 'Elsewhere'}
                  </span>
                  {group.branch &&
                  onReviewBranch &&
                  reviewBase &&
                  group.branch !== baseBranch &&
                  (!reviewBranch || group.branch === reviewBranch) ? (
                    <button
                      type="button"
                      onClick={() => {
                        onReviewBranch(group.branch as string)
                        setOpen(false)
                      }}
                      className="ml-auto shrink-0 rounded px-1 py-0.5 text-foreground hover:bg-muted"
                    >
                      Review
                    </button>
                  ) : null}
                </div>
              ) : null}
              {group.files.map(({ change, codeId: recorded }) => {
                const named = fileLabel(change, folder, fileSourceId)
                const codeId = fileSourceId
                  ? named.codeId
                  : (recorded ?? named.codeId)
                const label = named.outside ? change.label : named.label
                return (
                  <div
                    key={change.path}
                    title={change.path}
                    className="flex items-center gap-2 px-3 py-1 text-[13px]"
                  >
                    <FileCode2 className="size-3 shrink-0 text-muted-foreground/70" />
                    {/* The same open the sidebar's code tree does: a code id
                      handed to the canvas, which reads it from the git
                      server. A file no worktree claims stays a label. */}
                    {codeId && onOpenDocument ? (
                      <button
                        type="button"
                        onClick={() => {
                          onOpenDocument(codeId)
                          setOpen(false)
                        }}
                        className="truncate text-foreground/90 hover:underline"
                      >
                        {label}
                      </button>
                    ) : (
                      <span className="truncate text-foreground/90">
                        {label}
                      </span>
                    )}
                    <Trail change={change} />
                  </div>
                )
              })}
            </div>
          ))}
          {changes.files.length > 0 && changes.documents.length > 0 ? (
            <div className="my-1 border-border/60 border-t" />
          ) : null}
          {changes.documents.map((doc) => {
            const label = doc.documentId
              ? (titles.get(doc.documentId) ?? doc.label)
              : doc.label
            const openDoc =
              doc.documentId && onOpenDocument
                ? () => onOpenDocument(doc.documentId as string)
                : undefined
            return (
              <div
                key={doc.documentId ?? doc.label}
                className="flex items-center gap-2 px-3 py-1 text-[13px]"
              >
                <FileText className="size-3 shrink-0 text-muted-foreground/70" />
                {/* Text when there is nothing to open. A button that does
                    nothing is the worse half of "a document with no id": at
                    least a label says what it is. */}
                {openDoc ? (
                  <button
                    type="button"
                    onClick={openDoc}
                    className="truncate text-foreground/90 hover:underline"
                  >
                    {label}
                  </button>
                ) : (
                  <span className="truncate text-foreground/90">{label}</span>
                )}
                <Trail change={doc} />
              </div>
            )
          })}
          {onViewMore ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                onViewMore()
              }}
              className="mt-1 block w-full border-border/60 border-t px-3 pt-2 pb-1 text-left text-[12px] text-foreground hover:bg-muted/60"
            >
              View more →
            </button>
          ) : null}
          {partial ? (
            <div className="mt-1 border-border/60 border-t px-3 pt-1.5 pb-0.5 text-[11px] text-muted-foreground">
              Counted from the messages loaded so far. Load earlier messages for
              the rest.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** What happened to one row: created, and how many times it was touched. The
 *  touch count earns its place only past one, where it says something. */
function Trail({
  change,
}: {
  change: { kind: 'created' | 'edited'; touches: number }
}) {
  return (
    <span className="ml-auto shrink-0 pl-2 text-[11px] text-muted-foreground/60">
      {change.kind === 'created' ? 'new' : ''}
      {change.kind === 'created' && change.touches > 1 ? ' · ' : ''}
      {change.touches > 1 ? `${change.touches} edits` : ''}
    </span>
  )
}
